import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Network } from 'vis-network/standalone';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppConfig } from '../../context/AppConfigContext';
import styles from './Neirotopics.module.css';
import NewUserHint from '../NewUserHint/NewUserHint';

// Импорты изображений тем
import dailyImg from '../../assets/images/500x500/daily.png';
import workImg from '../../assets/images/500x500/work.png';
import foodAndDrinksImg from '../../assets/images/500x500/food_and_drinks.png';
import clothingImg from '../../assets/images/500x500/clothing.png';
import transportImg from '../../assets/images/500x500/transport.png';
import weatherImg from '../../assets/images/500x500/weather.png';
import doctorImg from '../../assets/images/500x500/doctor.png';
import inTheCityImg from '../../assets/images/500x500/in_the_city.png';
import vacationImg from '../../assets/images/500x500/vacation.png';
import supermarketImg from '../../assets/images/500x500/supermarket.png';
import bankAndMoneyImg from '../../assets/images/500x500/bank_and_money.png';
import humanBodyImg from '../../assets/images/500x500/human_body.png';
import familyImg from '../../assets/images/500x500/family.png';
import natureImg from '../../assets/images/500x500/nature.png';
import animalsAndBirdsImg from '../../assets/images/500x500/animals_and_birds.png';

// Объект с соответствием кодов и изображений
const topicImages = {
  daily: dailyImg,
  work: workImg,
  food_and_drinks: foodAndDrinksImg,
  clothing: clothingImg,
  transport: transportImg,
  weather: weatherImg,
  doctor: doctorImg,
  in_the_city: inTheCityImg,
  vacation: vacationImg,
  supermarket: supermarketImg,
  bank_and_money: bankAndMoneyImg,
  human_body: humanBodyImg,
  family: familyImg,
  nature: natureImg,
  animals_and_birds: animalsAndBirdsImg,
};

// Базовые данные тем (визуальные и связи)
const topics = [
  { code: 'daily', img: topicImages.daily, relatedTo: ['work', 'family', 'weather'] },
  { code: 'work', img: topicImages.work, relatedTo: ['daily', 'bank_and_money'] },
  { code: 'food_and_drinks', img: topicImages.food_and_drinks, relatedTo: ['supermarket', 'family'] },
  { code: 'clothing', img: topicImages.clothing, relatedTo: ['supermarket', 'vacation'] },
  { code: 'transport', img: topicImages.transport, relatedTo: ['in_the_city', 'vacation'] },
  { code: 'weather', img: topicImages.weather, relatedTo: ['daily', 'vacation', 'nature'] },
  { code: 'doctor', img: topicImages.doctor, relatedTo: ['human_body', 'family'] },
  { code: 'in_the_city', img: topicImages.in_the_city, relatedTo: ['transport', 'supermarket', 'bank_and_money'] },
  { code: 'vacation', img: topicImages.vacation, relatedTo: ['weather', 'transport', 'clothing', 'nature'] },
  { code: 'supermarket', img: topicImages.supermarket, relatedTo: ['food_and_drinks', 'clothing', 'in_the_city'] },
  { code: 'bank_and_money', img: topicImages.bank_and_money, relatedTo: ['in_the_city', 'work'] },
  { code: 'human_body', img: topicImages.human_body, relatedTo: ['doctor'] },
  { code: 'family', img: topicImages.family, relatedTo: ['food_and_drinks', 'doctor', 'daily'] },
  { code: 'nature', img: topicImages.nature, relatedTo: ['animals_and_birds', 'weather', 'vacation'] },
  { code: 'animals_and_birds', img: topicImages.animals_and_birds, relatedTo: ['nature'] }
];

// Создание изображения с нужной прозрачностью
const createImageWithOpacity = (src, opacity) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.globalAlpha = opacity;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL());
    };
    img.src = src;
  });
};

// Расчет прогресса
const getProgressFromTopic = (topic, source) => {
  if (source === 'dialogs' || source === 'exam') {
    const attempts = topic.attemptNumber || 0;
    return Math.min(1, attempts * 0.3); // 30% за каждую попытку
  }

  const total = topic?.blocks?.length || 0;
  const completed = Object.values(topic?.completed_block_ids || {}).reduce((sum, val) => sum + val, 0);
  return total === 0 ? 0 : completed / total;
};


const Neirotopics = ({ data = [], onSelect, neirotitle="Choise topic", source="lesson"}) => {
  const networkRef = useRef(null);
  const containerRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const { t, updateConfig } = useAppConfig();
  const [randomSelected, setRandomSelected] = useState(false);
  const randomTimeoutRef = useRef(null);
  const [newUserHintClosed, setNewUserHintClosed] = useState(false); 
  const [shouldShowHint, setShouldShowHint] = useState(false);

	//console.log('data:', data);
  
	// случайный выбор темы и топика
	const handleRandomSelect = () => {
	  const randomTopic = mergedTopics[Math.floor(Math.random() * mergedTopics.length)];
	  setSelectedNode(randomTopic.code);
	  setRandomSelected(true);

	  // Автоматически выбираем через 5 сек
	  if (randomTimeoutRef.current) clearTimeout(randomTimeoutRef.current);

	  randomTimeoutRef.current = setTimeout(() => {
		const block = randomTopic.blocks?.[0]; // первый блок или undefined
		onSelect?.(randomTopic.id, block?.id);
	  }, 5000);
	};
	// Очистка таймера при размонтировании
	useEffect(() => {
	  return () => {
		if (randomTimeoutRef.current) clearTimeout(randomTimeoutRef.current);
	  };
	}, []);
	
  
   // Объединение данных
	const mergedTopics = useMemo(() => {
	  return topics.map(base => {
		const fullData = data.find(item => item.code === base.code);
		const progress = fullData ? getProgressFromTopic(fullData, source) : 0;
		const name = fullData?.title || base.code;

		return {
		  ...base,
		  ...fullData,
		  progress,
		  name,
		  id: fullData?.id,
		  blocks: fullData?.blocks || [],
		};
	  });
	}, [topics, data, source]);

	useEffect(() => {
	  const allZero = mergedTopics.every(topic => topic.progress === 0);
	  updateConfig({ isNewUserInModule: allZero });
	  setShouldShowHint(allZero); 
	  //console.log("Ваш текущий прогресс в нейротопик: ", allZero);
	}, [mergedTopics]);

	
   const selectedTopic = mergedTopics.find(t => t.code === selectedNode);
   

  // Инициализация карты
	useEffect(() => {
	  if (!newUserHintClosed && shouldShowHint) return;	
	  const loadNetwork = async () => {
		if (!containerRef.current) return; // дополнительная защита  
		if (containerRef.current) {
		  containerRef.current.style.width = '100%';
		  containerRef.current.style.height = '100%';
		}
		const nodes = await Promise.all(
		  mergedTopics.map(async (topic) => {
			const opacity = 0.2 + topic.progress * 0.8;
			const imageUrl = await createImageWithOpacity(topic.img, opacity);
			return {
			  id: topic.code,
			  image: imageUrl,
			  shape: 'circularImage',
			  size: 40,
			  borderWidth: 2,
			  label: '',
			  font: { size: 0 },
			  color: {
				border: '#1E3A8A',
				background: '#FFFFFF',
			  },
			  shadow: {
				enabled: true,
				color: `rgba(71, 167, 249, ${topic.progress * 0.5})`,
				size: 5 + topic.progress * 5,
			  },
			};
		  })
		);

		const edgesSet = new Set();
		const edges = [];

		mergedTopics.forEach((topic) => {
		  topic.relatedTo.forEach((relatedCode) => {
			const key = [topic.code, relatedCode].sort().join('-');
			if (!edgesSet.has(key) && mergedTopics.find(t => t.code === relatedCode)) {
			  edgesSet.add(key);
			  edges.push({
				from: topic.code,
				to: relatedCode,
				color: { color: '#1E3A8A' },
				smooth: { type: 'curvedCW', roundness: 0.2 },
			  });
			}
		  });
		});

		const data = { nodes, edges };
		const options = {
		  autoResize: false,
		  nodes: { shape: 'circularImage', borderWidth: 2 },
		  edges: { width: 1 },
		  physics: {
			enabled: true,
			forceAtlas2Based: {
			  gravitationalConstant: -50,
			  centralGravity: 0.01,
			  springLength: 100,
			},
			minVelocity: 0.75,
		  },
		  interaction: {
			zoomView: true,
			dragView: true,
			hover: true,
			zoomSpeed: 0.5,
		  },
		};

		networkRef.current = new Network(containerRef.current, data, options);

		networkRef.current.on('click', (params) => {
		  if (params.nodes.length > 0) {
			const nodeCode = params.nodes[0];
			setSelectedNode(nodeCode);
			networkRef.current.focus(nodeCode, {
			  scale: 1.5,
			  animation: { duration: 500, easingFunction: 'easeInOutQuad' },
			});
		  } else {
			setSelectedNode(null);
			networkRef.current.fit({ animation: { duration: 500 } });
		  }
		});
	  };

	  loadNetwork();

	  return () => networkRef.current?.destroy();
	}, [data, newUserHintClosed, shouldShowHint]);

	return (
	  <div className={styles.container}>
	    {!newUserHintClosed && shouldShowHint && (
		  <NewUserHint
			source={`${source}_neiro`}
			onClose={() => setNewUserHintClosed(true)}
		  />
		)}
		<div className={styles.headerContainer}>
		  {!selectedTopic && <h2 className={styles.title}>{neirotitle}</h2>}
		  <div className={styles.randomContainer}>
			{!selectedTopic && (
			  <p className={styles.randomLabel}>
				{randomSelected 
				  ? (t.neirotopic?.reselect || 'Перевыбрать') 
				  : (t.neirotopic?.press_random || 'или нажмите Random')}
			  </p>
			)}
			<button
			  onClick={handleRandomSelect}
			  className={`${styles.randomButton} ${randomSelected ? styles.active : ''}`}
			>
			  🎲
			  {randomSelected && <div className={styles.timerFill} />}
			</button>
		  </div>
		</div>

		{randomSelected && selectedTopic && (
		  <div className={styles.randomResult}>
			<strong>{selectedTopic.name}</strong>
			{selectedTopic.blocks?.length > 0 && (
			  <>
				<br />
				<span className={styles.selectedBlock}>
				  {selectedTopic.blocks[0]?.title || 'Untitled'}
				</span>
			  </>
			)}
		  </div>
		)}

		<div ref={containerRef} className={styles.network} />
		<AnimatePresence>
		  {selectedNode && !randomSelected && (
			<motion.div
			  className={styles.selectedCard}
			  initial={{ opacity: 0, y: -20 }}
			  animate={{ opacity: 1, y: 0 }}
			  exit={{ opacity: 0, y: -20 }}
			  transition={{ duration: 0.3 }}
			>
			  <h3 className={styles.cardTitle}>{selectedTopic?.name}</h3>
			  {/* рендеринг прогресса */}
				{source === 'dialogs' && selectedTopic?.attemptNumber > 0 && (
				  <p className={styles.cardText}>
					{t.lessons.progress}: <span className={styles.checks}>{'✔'.repeat(selectedTopic.attemptNumber)}</span>
				  </p>
				)}

				{source !== 'dialogs' && (
				  <p className={styles.cardText}>
					{t.lessons.progress}: {(selectedTopic?.progress * 100).toFixed(0)}%
				  </p>
				)}
		  		<ul className={styles.submenu}>
				  {selectedTopic?.blocks?.length > 0 &&
					selectedTopic.blocks.map(block => {
					  const completedCount = selectedTopic?.completed_block_ids?.[block.id] || 0;

					  const handleClick = () => {
						if (source !== 'lesson' || !block.id) {
						  onSelect?.(selectedTopic.id);
						} else {
						  onSelect?.(selectedTopic.id, block.id);
						}
					  };

					  return (
						<li key={block.id} className={styles.submenuItem} onClick={handleClick}>
						  <span>{block.title || 'Untitled'}</span>
						  {completedCount > 0 && (
							<span className={styles.checks}>{'✔'.repeat(completedCount)}</span>
						  )}
						</li>
					  );
					})}
				</ul>

				{/* Отдельная кнопка, если блоков нет */}
				{selectedTopic?.blocks?.length === 0 && (
				  <button className={`${styles.cardText} ${styles.startButton}`} onClick={() => onSelect?.(selectedTopic.id)} >
					{t.dialog.start}
				  </button>
				)}


			</motion.div>
		  )}
		</AnimatePresence>
	  </div>
	);
};

export default Neirotopics;